import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useReduxAuth';
import { usePermissions } from './usePermissions';
import { SCREENS, ACTIONS } from '../constants/permissions';
import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_ENTITY_ALERT_IDS } from '../graphql/queries/alerts';

const ENTITY_TYPE_TO_SCREEN = {
  invoice: SCREENS.INVOICE_MANAGEMENT,
  job: SCREENS.JOB_MANAGEMENT,
  leave: SCREENS.LEAVE_REQUESTS,
};

export const useEntityAlertMap = (entityType) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const [alertMap, setAlertMap] = useState({});

  useEffect(() => {
    if (!user?.companyId || !entityType) return;
    const screen = ENTITY_TYPE_TO_SCREEN[entityType];
    if (screen && user?.role !== 'ROOT' && !hasPermission(screen, ACTIONS.VIEW)) {
      setAlertMap({});
      return;
    }
    hasuraClient.query(GET_ENTITY_ALERT_IDS, {
      companyId: parseInt(user.companyId),
      entityType,
    })
      .then(res => {
        const map = {};
        const order = { critical: 3, warning: 2, info: 1 };
        (res.alert_instances || []).forEach(inst => {
          const sev = inst.alert_type_config?.severity || 'warning';
          if (!map[inst.entity_id] || (order[sev] || 0) > (order[map[inst.entity_id]] || 0)) {
            map[inst.entity_id] = sev;
          }
        });
        setAlertMap(map);
      })
      .catch(() => {});
  }, [user?.companyId, user?.role, entityType, hasPermission]);

  const getRowSx = useCallback((row) => {
    const severity = alertMap[String(row.id)];
    if (!severity) return {};
    const color = severity === 'critical'
      ? theme.palette.error.main
      : severity === 'warning'
        ? theme.palette.warning.dark
        : theme.palette.info.main;
    return {
      bgcolor: alpha(color, 0.12),
      '&:nth-of-type(odd)': { bgcolor: alpha(color, 0.12) },
      '&:hover': { bgcolor: alpha(color, 0.2) },
      '&:nth-of-type(odd):hover': { bgcolor: alpha(color, 0.2) },
      borderLeft: `3px solid ${color}`,
    };
  }, [alertMap, theme]);

  return { alertMap, getRowSx };
};
